import { useState, useId, MouseEvent } from "react";
import { Link } from "react-router-dom";
import { Button, IconButton, SvgIcon, Menu } from "@mui/material";

import "./style.scss";
import { ReactComponent as MenuIcon } from "../../assets/menu.icon.svg";
import { ReactComponent as CrossIcon } from "../../assets/cross.icon.svg";
import { Env } from "../../common/enums/env.enum";
import { Logo } from "../../common/enums/logo.enum";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import { isMobileDevice } from "../../common/utilities/platform/platform.util";
import { getHederLogoImg } from "../../common/utilities/logo/logo.util";
import { getEnvName } from "../../common/utilities/env/env.util";
import { SelectionFilterT } from "../menu-filter/menu-filter.comp";
import { ModalTemplate } from "../modal-template/modal-template.comp";
import FeedbackView from "../../views/admin/feedback/feedback";

const Header = () => {
    const { isTrial, user, knowledgeBase, version, logout } = FirebaseAuthContainer.useContainer();
    const { isLightTheme, headerLogo, devMode, devEnv, changeDevEnv, setLeftMenuShown } =
        ThemeContainer.useContainer();
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);
    const avatarButtonId = useId();
    const [upgradeScreenOpen, setUpgradeScreenOpen] = useState<boolean>(false);

    const handleLeftMenuClick = () => {
        setLeftMenuShown(true);
    };

    const handleProfileMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleSignOut = () => {
        handleProfileMenuClose();
        logout();
    };

    const handleUpgradeScreenOpen = () => {
        setUpgradeScreenOpen(true);
    };

    const handleUpgradeScreenClose = () => {
        setUpgradeScreenOpen(false);
    };

    return (
        <div className="header">
            <IconButton
                className="header-left-menu"
                aria-label="Toggle menu"
                onClick={handleLeftMenuClick}
            >
                <SvgIcon component={MenuIcon} inheritViewBox />
            </IconButton>
            <Link
                className={`header-logo${headerLogo === Logo.mba ? " header-logo--mba" : ""}`}
                to="/"
            >
                <img src={getHederLogoImg(headerLogo, !isLightTheme)} alt="Home" />
            </Link>
            {isTrial && (
                <div className="header-trial">
                    {knowledgeBase?.expiringTrialPeriod &&
                    knowledgeBase?.expiringTrialPeriod > 0 ? (
                        <>
                            <span className="header-trial__text">
                                Trial version expires in {knowledgeBase.expiringTrialPeriod} days
                            </span>
                            <Button
                                className="trial-button header-trial__button"
                                variant="text"
                                onClick={handleUpgradeScreenOpen}
                            >
                                Upgrade
                            </Button>
                        </>
                    ) : (
                        <span className="header-trial__text--warning">
                            Trial version has expired
                        </span>
                    )}
                </div>
            )}
            {user && (
                <div
                    className={`header-profile${isMobileDevice() ? " header-profile__mobile" : ""}`}
                >
                    <button
                        type="button"
                        id={avatarButtonId}
                        className="header-profile-avatar"
                        aria-label="Account menu"
                        aria-haspopup="true"
                        aria-expanded={menuOpen}
                        onClick={handleProfileMenuClick}
                    >
                        {user.avatar ? (
                            <img
                                src={user.avatar}
                                alt=""
                                referrerPolicy="no-referrer"
                                className="header-profile-avatar_img"
                            ></img>
                        ) : (
                            <div className="header-profile-avatar_label">
                                {user.firstName?.toUpperCase().at(0) ??
                                    user.email.toUpperCase().at(0)}
                            </div>
                        )}
                    </button>
                    <Menu
                        id="basic-menu"
                        anchorEl={menuAnchor}
                        open={menuOpen}
                        onClose={handleProfileMenuClose}
                        MenuListProps={{
                            "aria-labelledby": avatarButtonId
                        }}
                        slotProps={{
                            paper: {
                                elevation: 0,
                                className: "profile-menu"
                            }
                        }}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                        <div className="profile-menu-content">
                            <IconButton
                                className="profile-menu-button-close"
                                aria-label="Close"
                                onClick={handleProfileMenuClose}
                            >
                                <CrossIcon />
                            </IconButton>
                            <div className="profile-menu-email">{user.email}</div>
                            <div className="profile-menu-actions">
                                {devMode && devEnv && (
                                    <div className="profile-menu-actions__selection">
                                        <SelectionFilterT
                                            list={Object.values(Env)
                                                .map(env => ({
                                                    label: getEnvName(env),
                                                    value: env
                                                }))
                                                .filter(env => env.value !== Env.trial)}
                                            selectedItem={{
                                                label: getEnvName(devEnv),
                                                value: devEnv
                                            }}
                                            setSelectedItem={env => changeDevEnv(env.value as Env)}
                                            rightPosition={true}
                                            minWidth={145}
                                        />
                                    </div>
                                )}
                                <Button
                                    className="profile-menu-actions__button--logout primary-button"
                                    variant="outlined"
                                    onClick={handleSignOut}
                                >
                                    Sign Out
                                </Button>
                            </div>
                            {version && (
                                <div className="profile-menu-version">version: {version}</div>
                            )}
                            {user.currentKnowledgeBaseId && (
                                <div className="profile-menu-knowledgebase">
                                    ID: {user.currentKnowledgeBaseId}
                                </div>
                            )}
                        </div>
                    </Menu>
                </div>
            )}
            <ModalTemplate
                open={upgradeScreenOpen}
                width={654}
                handleClose={handleUpgradeScreenClose}
                ariaLabel="Upgrade to full version"
            >
                <FeedbackView handleClose={handleUpgradeScreenClose} />
            </ModalTemplate>
        </div>
    );
};

export default Header;
